import { StateStorage } from 'zustand/middleware';

const DB_NAME = 'DramaFlicks-db';
const STORE_NAME = 'zustand-state';
const DB_VERSION = 1;


let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return Promise.reject(new Error('IndexedDB is not available in this environment'));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          console.warn("Database is outdated. Please reload the page.");
          dbPromise = null;
        };
        resolve(db);
      };

      request.onerror = () => {
        reject(request.error);
        dbPromise = null; 
      };

      request.onblocked = () => {
        console.warn('IndexedDB connection is blocked. Please close other tabs with this site open.');
        reject(new Error('IndexedDB connection blocked'));
        dbPromise = null;
      };
    });
  }
  return dbPromise;
}

/**
 * A custom storage object for Zustand's persist middleware that uses the native IndexedDB API.
 * This allows for storing much larger amounts of data than localStorage without external packages.
 */
export const idbStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return null;
    }
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(name) as IDBRequest<string | undefined>;

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result === undefined ? null : request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return;
    }
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(value, name);
  },
  removeItem: async (name: string): Promise<void> => {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return;
    }
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete(name);
  },
};